<template>
    <HamletStaticTable
        :columns="columns"
        :items="localValue"
        :striped="false"
        :bordered="true"
        empty-mode="message"
        card-class="pt-0 mt-0"
        table-container-class="table-responsive hamlet-table product-links-table"
    >
        <template #header-label>
            Product Links
        </template>

        <template #header-controls>
            <SupplierProductLookupSelect
                v-if="hasSupplierId"
                :supplier-id="supplierId"
                :placeholder="$t('Component(IncludedProduct):search-placeholder')"
                @select="addProductLink"
            />
            <small
                v-else
                class="text-muted"
            >{{ $t('Component(ReviewProcess):supplier-context-missing') }}</small>
        </template>

        <template #cell-productCode="{ item }">
            <span>{{ selectedProductCode(item) }}</span>
        </template>

        <template #cell-productName="{ item }">
            <span>{{ selectedProductName(item) }}</span>
        </template>

        <template #cell-linkedProduct="{ item }">
            <span>{{ linkedProductLabel(item) }}</span>
        </template>

        <template #cell-actions="{ item }">
            <CommonButton
                variant="danger"
                size="sm"
                class="btn-icon"
                @click="removeRow(item)"
            >
                <feather-icon
                    icon="MinusIcon"
                    size="14"
                />
            </CommonButton>
        </template>
    </HamletStaticTable>
</template>

<script>
import { HamletStaticTable, Button as CommonButton } from '@core/components/common'
import SupplierProductLookupSelect from './components/SupplierProductLookupSelect.vue'

function clone(value) {
    return JSON.parse(JSON.stringify(value || []))
}

function isEqual(a, b) {
    return JSON.stringify(a) === JSON.stringify(b)
}

function normalize(items) {
    return Array.isArray(items)
        ? clone(items).map(item => {
            const row = item && typeof item === 'object' ? item : {}

            return {
                extractedProductId: row.extractedProductId !== undefined ? row.extractedProductId : null,
                productId: row.productId !== undefined && row.productId !== null ? String(row.productId) : '',
                type: row.type || 'manual',
                status: row.status || 'accepted',
                confidence: row.confidence !== undefined ? row.confidence : null,
                reason: row.reason !== undefined ? row.reason : null,
            }
        })
        : []
}

function emptyRow(product) {
    return {
        extractedProductId: null,
        productId: product && product.productId ? String(product.productId) : '',
        type: 'manual',
        status: 'accepted',
        confidence: null,
        reason: null,
    }
}

export default {
    name: 'ProductLinksSection',
    components: {
        HamletStaticTable,
        CommonButton,
        SupplierProductLookupSelect,
    },
    props: {
        value: {
            type: Array,
            default: () => [],
        },
        products: {
            type: Array,
            default: () => [],
        },
        supplierId: {
            type: [String, Number],
            default: null,
        },
    },
    data() {
        return {
            localValue: normalize(this.value),
            productLookupById: {},
            columns: [
                {
                    key: 'productCode', label: this.$t('Component(Grid):column-id'), thClass: 'product-links-col-code', tdClass: 'product-links-col-code',
                },
                {
                    key: 'productName', label: this.$t('Component(Grid):column-title'), thClass: 'product-links-col-name', tdClass: 'product-links-col-name',
                },
                {
                    key: 'linkedProduct', label: this.$t('Component(ResearchReviewProcess):linked-extracted-product'), thClass: 'product-links-col-linked', tdClass: 'product-links-col-linked',
                },
                {
                    key: 'actions', label: '', thClass: 'text-right product-links-col-actions', tdClass: 'text-right product-links-col-actions',
                },
            ],
        }
    },
    computed: {
        hasSupplierId() {
            return !!this.supplierId
        },
        productsByExtractedId() {
            return this.products.reduce((acc, product, index) => {
                if (!product || product.extractedProductId === undefined || product.extractedProductId === null) {
                    return acc
                }

                acc[String(product.extractedProductId)] = {
                    code: product.code || '',
                    name: product.name || (`Product ${index + 1}`),
                }

                return acc
            }, {})
        },
    },
    watch: {
        value: {
            deep: true,
            handler(newValue) {
                const normalized = normalize(newValue)
                if (isEqual(normalized, this.localValue)) {
                    return
                }

                this.localValue = normalized
            },
        },
        localValue: {
            deep: true,
            handler(newValue) {
                const emitted = clone(newValue).map(item => ({
                    extractedProductId: item.extractedProductId !== undefined ? item.extractedProductId : null,
                    productId: item.productId || '',
                    type: item.type || 'manual',
                    status: item.status || 'accepted',
                    confidence: item.confidence !== undefined ? item.confidence : null,
                    reason: item.reason !== undefined ? item.reason : null,
                }))
                const current = normalize(this.value)

                if (isEqual(emitted, current)) {
                    return
                }

                this.$emit('input', emitted)
            },
        },
    },
    methods: {
        removeRow(item) {
            const index = this.localValue.indexOf(item)
            if (index === -1) {
                return
            }

            this.localValue.splice(index, 1)
        },
        addProductLink(product) {
            if (!product || !product.productId) {
                return
            }

            const productId = String(product.productId)
            const exists = this.localValue.some(item => item && String(item.productId || '') === productId)
            if (exists) {
                return
            }

            this.$set(this.productLookupById, productId, {
                code: product.code || productId,
                name: product.name || product.title || productId,
            })
            this.localValue.push(emptyRow(product))
        },
        linkedProductLabel(item) {
            if (!item || item.extractedProductId === undefined || item.extractedProductId === null) {
                return '-'
            }

            const linked = this.productsByExtractedId[String(item.extractedProductId)]
            if (!linked) {
                return '-'
            }

            return [linked.name, linked.code].filter(Boolean).join(' | ')
        },
        selectedProductCode(item) {
            if (!item || !item.productId) {
                return '-'
            }

            const product = this.productLookupById[item.productId]
            return product && product.code ? product.code : item.productId
        },
        selectedProductName(item) {
            if (!item || !item.productId) {
                return '-'
            }

            const product = this.productLookupById[item.productId]
            return product && product.name ? product.name : item.productId
        },
    },
}
</script>

<style scoped>
::v-deep .product-links-table table {
    min-width: 700px;
}

::v-deep .product-links-table .product-links-col-code {
    min-width: 150px;
}

::v-deep .product-links-table .product-links-col-name {
    min-width: 150px;
}

::v-deep .product-links-table .product-links-col-linked {
    width: 300px;
    min-width: 300px;
    max-width: 300px;
}

::v-deep .product-links-table .product-links-col-actions {
    width: 50px;
    min-width: 50px;
}
</style>
